import { useEffect, useState, useRef } from "react";
import { Upload, FileText, Download, Trash2, FolderOpen } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { fdate } from "@/lib/format";

const CATS = ["Carte grise", "Assurance", "Contrat", "Facture", "Contrôle technique", "Photo", "Sinistre", "Autre"];

export default function Documents() {
  const [docs, setDocs] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [vehicleId, setVehicleId] = useState("");
  const [category, setCategory] = useState("Autre");
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/documents").then((r) => setDocs(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const vehLabel = (id) => { const v = vehicles.find((x) => x.id === id); return v ? `${v.brand} ${v.model} (${v.registration})` : "Général"; };

  const upload = async () => {
    if (!file) { toast.error("Sélectionnez un fichier"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    if (vehicleId) fd.append("vehicle_id", vehicleId);
    fd.append("category", category);
    fd.append("description", description);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document ajouté");
      setOpen(false); setFile(null); setDescription(""); load();
    } catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };

  const download = async (d) => {
    try {
      const res = await api.get(`/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (d) => {
    try { await api.delete(`/documents/${d.id}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Documents" subtitle="Gestion documentaire centralisée par véhicule.">
        <Button onClick={() => setOpen(true)} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="document-upload-btn"><Upload className="mr-2 h-4 w-4" />Ajouter un document</Button>
      </PageHeader>

      {docs === null ? <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        : docs.length === 0 ? <EmptyState icon={FolderOpen} title="Aucun document" description="Ajoutez cartes grises, assurances, contrats, factures…" />
        : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {docs.map((d) => (
              <div key={d.id} className="flex items-start gap-3 rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="document-card">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EEF2F8] text-[#001A3B]"><FileText className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-[#18221F]">{d.original_filename}</p>
                  <p className="text-xs text-[#8695AB]">{vehLabel(d.vehicle_id)}</p>
                  <div className="mt-1 flex items-center gap-2"><span className="rounded bg-[#F4EFE3] px-1.5 py-0.5 text-[10px] font-medium text-[#7a6323]">{d.category}</span><span className="text-[10px] text-[#8695AB]">{fdate(d.created_at)}</span></div>
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => download(d)} className="rounded p-1.5 text-[#4A5568] hover:bg-[#EEF2F8] hover:text-[#001A3B]" data-testid="document-download-btn"><Download className="h-4 w-4" /></button>
                  <button onClick={() => remove(d)} className="rounded p-1.5 text-[#4A5568] hover:bg-red-50 hover:text-red-600" data-testid="document-delete-btn"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">Ajouter un document</DialogTitle>
            <DialogDescription className="text-xs text-[#8695AB]">Choisissez un fichier PDF ou image (15 Mo maximum), puis renseignez la catégorie.</DialogDescription></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="mb-1.5 block text-sm">Fichier (PDF ou image, max 15 Mo)</Label>
              <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.gif" className="hidden" onChange={(e) => setFile(e.target.files[0] || null)} data-testid="document-file-input" />
              <div className="flex items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white p-2">
                <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="document-file-choose">Choisir un fichier</Button>
                <span className="min-w-0 flex-1 truncate text-sm text-[#4A5568]" data-testid="document-file-name">{file ? file.name : "Aucun fichier sélectionné"}</span>
              </div></div>
            <div><Label className="mb-1.5 block text-sm">Véhicule</Label>
              <Select value={vehicleId} onValueChange={setVehicleId}><SelectTrigger data-testid="document-vehicle"><SelectValue placeholder="Général / non lié" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.registration})</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-1.5 block text-sm">Catégorie</Label>
              <Select value={category} onValueChange={setCategory}><SelectTrigger data-testid="document-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="mb-1.5 block text-sm">Description</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="document-description" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={upload} disabled={uploading} className="bg-[#A5894E] hover:bg-[#8F7440]" data-testid="document-save-btn">{uploading ? "Envoi…" : "Téléverser"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
